import { OCSController, Application } from 'ocscontroller'
import { renderTwig } from 'ocstemplatemanager'
import { TextUtilities } from 'ocs_utilities'
import GraphicastsPanelTemplate from './static/html/graphicasts_panel_template.twig' with { type: 'text' }

export class OCSGraphicastsController extends OCSController {
    initialize() {
        super.initialize()
        this.dataTypes = {
            siteWfo: 'string',
        }
        this.graphicastsData = null
        this.refreshProcess = null
    }

    connect() {
        super.connect()
        this.load()
        this.startRefreshing()
        if (document.body.dataset.mesoStationWfo) {
            this.options.options.siteWfo = document.body.dataset.mesoStationWfo
        } else {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'data-meso-station-wfo'
                    ) {
                        observer.disconnect()
                        this.options.options.siteWfo =
                            document.body.dataset.mesoStationWfo
                        if (
                            this.graphicastsData &&
                            this.graphicastsData.length
                        ) {
                            this.onRequestSuccess(this.graphicastsData)
                        }
                    }
                }
            })
            observer.observe(document.body, { attributes: true })
        }
        this.element.addEventListener(
            'stationChanged',
            this.handleStationChange.bind(this),
        )
        document.addEventListener(
            'stationChanged',
            this.handleStationChange.bind(this),
        )
    }

    disconnect() {
        super.disconnect()
        this.stopRefreshing()
        this.unload()
    }

    load() {
        if (!this.hasGraphicastsTarget) {
            const div = document.createElement('div')
            div.setAttribute('data-graphicasts-target', 'graphicasts')
        }
    }

    unload() {
        const el = this.element
        el.textContent = ''
    }

    /**
     * Methods
     */

    /**
     * Request JSON data for NWS graphicasts from the Mesonet API
     * @returns {Promise} from onRequestSuccess
     */
    async requestData() {
        const wfoArray = this.wfoArray(this.options.options.siteWfo || 'oun')
        const promises = []
        wfoArray.forEach((office) => {
            const templateOpsForWFO = async () => {
                const ajaxOps = this.options.ajaxOptions
                const url = TextUtilities.stringReplace(ajaxOps.urlTemplate, {
                    apiHost: ajaxOps.apiHost,
                    wfo: office.toLowerCase(),
                })
                const response = await fetch(url)
                const json = await response.json()
                return {
                    wfo: office,
                    wfoName: this.nameForWFO(office),
                    wfoUrl: `https://www.weather.gov/${office}`,
                    classes: this.options.cssClasses,
                    graphicasts: json.data.graphicasts,
                    headingLevel: this.options.options.headingLevel,
                }
            }
            promises.push(templateOpsForWFO())
        })
        try {
            const results = await Promise.all(promises)
            return this.onRequestSuccess(results)
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * Refresh the data.  Used with a timer for auto-refresh
     * @returns {Promise} from requestData
     */
    refreshData() {
        return this.requestData()
    }

    /**
     * Add an identifier to each graphicast.
     */

    /**
     * Start auto-refreshing by setting interval call to refreshData
     */
    startRefreshing() {
        this.refreshData()
        const gcOptions = this.options.options
        if (!this.refreshProcess && gcOptions.refreshRate) {
            const rate = gcOptions.refreshRate * 1000
            this.refreshProcess = setInterval(this.refreshData.bind(this), rate)
        }
    }

    /**
     * Stop the auto-refresh by clearing the interval refresh process
     */
    stopRefreshing() {
        if (this.refreshProcess) {
            clearInterval(this.refreshProcess)
            this.refreshProcess = null
        }
        return this
    }

    /**
     * Render all the graphicasts for all WFOs
     * @returns {HTMLElement} the main container of all graphicasts
     */
    renderGraphicasts() {
        const mainContainer = this.element
        const gcData = this.graphicastsData
        mainContainer.innerHTML = ''
        const wfos = this.wfoArray(this.options.options.siteWfo)
        wfos.forEach((wfo) => {
            const templateOps = gcData.find(
                (data) => wfo.toLowerCase() === data.wfo.toLowerCase(),
            )
            const rendered = renderTwig(GraphicastsPanelTemplate, templateOps)
            mainContainer.insertAdjacentHTML('beforeend', rendered)
        })
        return mainContainer
    }

    /**
     * Return the graphicast data for a particular WFO.
     * @param {String} inWfoId
     * @returns {Object} all graphicasts for the WFO
     */
    findWFOData(inWfoId) {
        return this.graphicastsData.find((wfoItem) => {
            return wfoItem.wfo === inWfoId
        })
    }

    /**
     * Find and return a particular graphicast by index from the specified WFO
     * @param {Object} inWfoData
     * @param {int} inIndex
     * @returns {Object} single graphicast data
     */
    findGraphicast(inWfoData, inIndex) {
        const graphicasts = inWfoData.graphicasts
        return graphicasts.find((gc) => {
            return gc.graphicNumber.toString() === inIndex.toString()
        })
    }

    /**
     * Clear the graphicast title, image, and description from the modal
     */
    clearOverlay() {
        const modal = document.querySelector('#graphicasts-modal')
        const titleElement = modal.querySelector('.modal-title')
        titleElement.textContent = ''
        const bodyElement = modal.querySelector('.modal-body')
        bodyElement.textContent = ''
    }

    /**
     * Set up the overlay with the selected graphicast information
     * @param {Object} inGraphicast
     */
    setGraphicastImage(inGraphicast) {
        const modal = document.querySelector('#graphicasts-modal')
        const bodyElement = modal.querySelector('.modal-body')
        const imageUrl = inGraphicast.url.replace('http://', 'https://')
        const image = document.createElement('img')
        image.classList.add('graphicast-image-element', 'img-fluid')
        image.setAttribute('src', imageUrl)
        image.setAttribute('alt', '')
        bodyElement.appendChild(image)
        const titleElement = modal.querySelector('.modal-title')
        titleElement.textContent = inGraphicast.title
        const descriptionElement = document.createElement('p')
        descriptionElement.classList.add('pt-3', 'mb-0', 'text-start')
        descriptionElement.textContent = inGraphicast.description
        bodyElement.appendChild(descriptionElement)
        return modal
    }

    nameForWFO(wfo) {
        const wfoNames = {
            oun: 'Norman, OK',
            tsa: 'Tulsa, OK',
            ama: 'Amarillo, TX',
            shv: 'Shreveport, LA',
            fwd: 'Dallas / Fort Worth, TX',
            lub: 'Lubbock, TX',
            lzk: 'Little Rock, AR',
        }
        return wfoNames[wfo.toLowerCase()]
    }

    /**
     * Event listeners
     */

    /**
     * Respond to changes in the options stored in the DOM
     * @param {*} inMutations
     */
    onDOMChange(inMutations) {
        super.onDOMChange(inMutations)
        let attributesChange = false
        for (const mutation of inMutations) {
            if (mutation.type === 'attributes') {
                attributesChange = true
            }
        }
        if (attributesChange) {
            this.onRequestSuccess(this.graphicastsData)
        }
    }

    /**
     * Parse the XML data and set the local copy of data, then render the graphicasts
     * @param {XML} inData
     * @returns {HTMLElement} resolves a container with all the rendered graphicasts; logs if error thrown
     */
    onRequestSuccess(inData) {
        this.graphicastsData = inData
        const renderedContainer = this.renderGraphicasts()
        return renderedContainer
    }

    /**
     * Handle a mouse click on an image
     * @param {HTMLElement} inElement
     */
    onGraphicastSelected(inElement) {
        let result = null
        const target = inElement.target
        const wfo = target.getAttribute('data-gc-wfo')
        const index = target.getAttribute('data-gc-index')
        try {
            const wfoData = this.findWFOData(wfo)
            if (wfoData.graphicasts) {
                const wfoGraphicast = this.findGraphicast(wfoData, index)
                if (wfoGraphicast.graphicNumber) {
                    this.clearOverlay()
                    result = this.setGraphicastImage(wfoGraphicast)
                }
            }
        } catch (err) {
            console.error(err)
        }
        return result
    }

    wfoArray(firstWFO) {
        const array = this.options.options.displayWfos
        if (firstWFO) {
            const wfoIndex = array.indexOf(firstWFO)
            if (wfoIndex > 0) {
                array.splice(wfoIndex, 1)
                array.unshift(firstWFO)
                return array
            }
        }
        return array
    }

    handleStationChange(event) {
        const newWFO = event.detail.wfo || document.body.dataset.mesoStationWfo
        if (newWFO) {
            this.options.options.siteWfo = newWFO
            this.onRequestSuccess(this.graphicastsData)
        }
    }

    /**
     * Getters / Setters
     */

    get defaultOptions() {
        return {
            ajaxOptions: {
                apiHost: document.body.dataset.apiHost || 'api.mesonet.org',
                urlTemplate:
                    'https://{apiHost}/v2/products/nws/graphicasts?wfo={wfo}',
            },
            cssClasses: {
                heading: 'graphicast-title pt-4',
                panelsContainer: 'container-fluid',
                panelsRow: 'row',
                panelCell: 'col-xl-3 col-md-4 col-6',
                panelLink: 'text-decoration-none',
                panel: 'd-flex flex-column h-100 align-items-center',
                abovePanelImage: '',
                panelImageContainer: 'graphicast-thumbnail',
                panelImage: 'rounded overflow-hidden',
                belowPanelImage: '',
                panelTitleContainer: 'graphicast-title',
                panelTitle: 'pt-2 text-center font-strong fw-normal lh-sm',
            },
            options: {
                headingLevel: 'h3',
                refreshRate: 3600, // in seconds
                displayWfos: ['OUN', 'TSA', 'AMA', 'SHV'],
                siteWfo: 'OUN',
            },
        }
    }

    get graphicastsData() {
        return this._data
    }

    set graphicastsData(inData) {
        this._data = inData
    }
}

const stimulusApp = window.stimulusApp
    ? window.stimulusApp
    : Application.start()
stimulusApp.register('graphicasts', OCSGraphicastsController)
